import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Calendar, Clock, Users, Activity, 
  Waves, Music, Swords, Search, Filter, RefreshCw, Save,
  Megaphone, BarChart3, Sparkles, AlertCircle
} from 'lucide-react';

const API_URL = window.location.origin;

// Mapa de ícones por modalidade
const MODALITY_ICONS = {
  'karate': Swords,
  'pilates': Activity,
  'danca': Music,
  'natacao': Waves
};

// Dias da semana
const WEEKDAYS = [
  { id: 'segunda', name: 'Segunda-feira' },
  { id: 'terca', name: 'Terça-feira' },
  { id: 'quarta', name: 'Quarta-feira' },
  { id: 'quinta', name: 'Quinta-feira' },
  { id: 'sexta', name: 'Sexta-feira' }
];

// Períodos
const PERIODS = [
  { id: 'manha', name: 'Manhã' },
  { id: 'tarde', name: 'Tarde' }
];

export default function AulasExtrasAdmin() {
  const [activeTab, setActiveTab] = useState('horarios');
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [classes, setClasses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filtros
  const [filterWeekday, setFilterWeekday] = useState('');
  const [filterModality, setFilterModality] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Form data
  const [scheduleForm, setScheduleForm] = useState({
    weekday: 'segunda',
    modality_id: '',
    pedagogy_period: 'manha',
    start_time: '08:00',
    end_time: '09:00',
    departure_time: '',
    notes: '',
    is_contraturno: true,
    classes: [],
    display_order: 1
  });
  
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    active: true,
    priority: 1
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [schedulesRes, modalitiesRes, classesRes, announcementsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/schedules`),
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/modalities`),
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/classes`),
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/announcements`),
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/stats`)
      ]);
      
      setSchedules(await schedulesRes.json());
      setModalities(await modalitiesRes.json());
      setClasses(await classesRes.json());
      setAnnouncements(await announcementsRes.json());
      setStats(await statsRes.json());
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrar horários
  const filteredSchedules = schedules.filter(s => {
    if (filterWeekday && s.weekday !== filterWeekday) return false;
    if (filterModality && s.modality_id !== filterModality) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return s.class_names?.some(c => c.toLowerCase().includes(search)) ||
             s.modality_name?.toLowerCase().includes(search);
    }
    return true;
  });

  // Agrupar por dia da semana
  const schedulesByDay = WEEKDAYS.reduce((acc, day) => {
    acc[day.id] = filteredSchedules.filter(s => s.weekday === day.id)
      .sort((a, b) => a.display_order - b.display_order);
    return acc;
  }, {});

  // Handlers de Schedule
  const openScheduleDialog = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setScheduleForm({
        weekday: schedule.weekday,
        modality_id: schedule.modality_id,
        pedagogy_period: schedule.pedagogy_period,
        start_time: schedule.start_time,
        end_time: schedule.end_time,
        departure_time: schedule.departure_time || '',
        notes: schedule.notes || '',
        is_contraturno: schedule.is_contraturno,
        classes: schedule.classes || [],
        display_order: schedule.display_order || 1
      });
    } else {
      setEditingSchedule(null);
      setScheduleForm({
        weekday: 'segunda',
        modality_id: modalities[0]?.id || '',
        pedagogy_period: 'manha',
        start_time: '08:00',
        end_time: '09:00',
        departure_time: '',
        notes: '',
        is_contraturno: true,
        classes: [],
        display_order: 1
      });
    }
    setScheduleDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (!scheduleForm.modality_id || scheduleForm.classes.length === 0) {
      toast.error('Preencha a modalidade e selecione ao menos uma turma');
      return;
    }
    
    try {
      const url = editingSchedule 
        ? `${API_URL}/api/dnaconnect/aulas-extras/schedules/${editingSchedule.id}`
        : `${API_URL}/api/dnaconnect/aulas-extras/schedules`;
      
      const response = await fetch(url, {
        method: editingSchedule ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(scheduleForm)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao salvar');
      }
      
      toast.success(editingSchedule ? 'Horário atualizado!' : 'Horário criado!');
      setScheduleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!itemToDelete) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/dnaconnect/aulas-extras/schedules/${itemToDelete.id}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (!response.ok) throw new Error('Erro ao excluir');
      
      toast.success('Horário excluído!');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Handlers de Announcement
  const openAnnouncementDialog = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setAnnouncementForm({
        title: announcement.title,
        content: announcement.content,
        active: announcement.active,
        priority: announcement.priority
      });
    } else {
      setEditingAnnouncement(null);
      setAnnouncementForm({
        title: '',
        content: '',
        active: true,
        priority: 1
      });
    }
    setAnnouncementDialogOpen(true);
  };

  const handleSaveAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) {
      toast.error('Preencha o título e conteúdo');
      return;
    }
    
    try {
      const url = editingAnnouncement 
        ? `${API_URL}/api/dnaconnect/aulas-extras/announcements/${editingAnnouncement.id}`
        : `${API_URL}/api/dnaconnect/aulas-extras/announcements`;
      
      const response = await fetch(url, {
        method: editingAnnouncement ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(announcementForm)
      });
      
      if (!response.ok) throw new Error('Erro ao salvar');
      
      toast.success(editingAnnouncement ? 'Comunicado atualizado!' : 'Comunicado criado!');
      setAnnouncementDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteAnnouncement = async (announcement) => {
    if (!window.confirm('Excluir este comunicado?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/dnaconnect/aulas-extras/announcements/${announcement.id}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (!response.ok) throw new Error('Erro ao excluir');
      
      toast.success('Comunicado excluído!');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleClassSelection = (classId) => {
    setScheduleForm(prev => ({
      ...prev,
      classes: prev.classes.includes(classId)
        ? prev.classes.filter(c => c !== classId)
        : [...prev.classes, classId]
    }));
  };

  const getModalityIcon = (modalityId) => {
    const Icon = MODALITY_ICONS[modalityId] || Activity;
    return Icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="aulas-extras-admin">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-purple-600" />
            Aulas Extras 2026
          </h1>
          <p className="text-gray-500 mt-1">Gerencie os horários das atividades extracurriculares</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(stats.by_modality || {}).map(([key, mod]) => {
            const Icon = getModalityIcon(key);
            return (
              <Card key={key} className="border-l-4" style={{ borderLeftColor: mod.color }}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{mod.name}</p>
                      <p className="text-2xl font-bold" style={{ color: mod.color }}>{mod.count}</p>
                    </div>
                    <Icon className="h-8 w-8 opacity-50" style={{ color: mod.color }} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="horarios" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Horários
          </TabsTrigger>
          <TabsTrigger value="comunicados" className="flex items-center gap-2">
            <Megaphone className="h-4 w-4" />
            Comunicados
          </TabsTrigger>
          <TabsTrigger value="estatisticas" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Estatísticas
          </TabsTrigger>
        </TabsList>

        {/* Tab: Horários */}
        <TabsContent value="horarios" className="space-y-4">
          {/* Toolbar */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex flex-wrap gap-2 items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar turma..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <Select value={filterWeekday || "all"} onValueChange={(v) => setFilterWeekday(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Dia da semana" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os dias</SelectItem>
                      {WEEKDAYS.map(day => (
                        <SelectItem key={day.id} value={day.id}>{day.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterModality || "all"} onValueChange={(v) => setFilterModality(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Modalidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {modalities.map(mod => (
                        <SelectItem key={mod.id} value={mod.id}>{mod.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(filterWeekday || filterModality || searchTerm) && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setFilterWeekday(''); setFilterModality(''); setSearchTerm(''); }}
                    >
                      Limpar filtros
                    </Button>
                  )}
                </div>
                <Button onClick={() => openScheduleDialog()} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Horário
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Grid de horários por dia */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {WEEKDAYS.map(day => (
              <Card key={day.id} className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-purple-600 to-violet-600 text-white py-3">
                  <CardTitle className="text-sm font-medium">{day.name}</CardTitle>
                  <CardDescription className="text-purple-100 text-xs">
                    {schedulesByDay[day.id]?.length || 0} horários
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-2 space-y-2 max-h-[500px] overflow-y-auto">
                  {schedulesByDay[day.id]?.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-4">Nenhum horário</p>
                  ) : (
                    schedulesByDay[day.id]?.map(schedule => {
                      const Icon = getModalityIcon(schedule.modality_id);
                      return (
                        <div 
                          key={schedule.id}
                          className="p-3 rounded-lg border bg-white hover:shadow-md transition-shadow"
                          style={{ borderLeftWidth: '4px', borderLeftColor: schedule.modality_color }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" style={{ color: schedule.modality_color }} />
                              <span className="font-medium text-sm">{schedule.modality_name}</span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {schedule.is_contraturno ? 'Contra' : 'Grade'}
                            </Badge>
                          </div>
                          <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                            <Clock className="h-3 w-3" />
                            {schedule.start_time} - {schedule.end_time}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {schedule.class_names?.slice(0, 3).map((cls, i) => (
                              <Badge key={i} variant="secondary" className="text-xs py-0">
                                {cls}
                              </Badge>
                            ))}
                            {schedule.class_names?.length > 3 && (
                              <Badge variant="secondary" className="text-xs py-0">
                                +{schedule.class_names.length - 3}
                              </Badge>
                            )}
                          </div>
                          {schedule.notes && (
                            <p className="mt-1 text-xs text-orange-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {schedule.notes}
                            </p>
                          )}
                          <div className="mt-2 flex gap-1 justify-end">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0"
                              onClick={() => openScheduleDialog(schedule)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => { setItemToDelete(schedule); setDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab: Comunicados */}
        <TabsContent value="comunicados" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openAnnouncementDialog()} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-2" />
              Novo Comunicado
            </Button>
          </div>
          
          <div className="grid gap-4">
            {announcements.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum comunicado cadastrado</p>
                </CardContent>
              </Card>
            ) : (
              announcements.map(ann => (
                <Card key={ann.id} className={`${ann.active ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50/30'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{ann.title}</h3>
                          <Badge variant={ann.active ? 'default' : 'secondary'}>
                            {ann.active ? 'Ativo' : 'Inativo'}
                          </Badge>
                          <Badge variant="outline">Prioridade: {ann.priority}</Badge>
                        </div>
                        <p className="mt-2 text-gray-600 whitespace-pre-line">{ann.content}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openAnnouncementDialog(ann)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteAnnouncement(ann)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Tab: Estatísticas */}
        <TabsContent value="estatisticas">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Resumo Geral</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="font-medium">Total de Horários</span>
                  <span className="text-2xl font-bold text-purple-600">{stats?.schedules_count || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="font-medium">Modalidades Ativas</span>
                  <span className="text-2xl font-bold text-blue-600">{stats?.modalities_count || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="font-medium">Turmas Cadastradas</span>
                  <span className="text-2xl font-bold text-green-600">{classes.length}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                  <span className="font-medium">Comunicados Ativos</span>
                  <span className="text-2xl font-bold text-orange-600">
                    {announcements.filter(a => a.active).length}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Por Modalidade</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(stats?.by_modality || {}).map(([key, mod]) => {
                  const Icon = getModalityIcon(key);
                  const percentage = stats?.schedules_count 
                    ? Math.round((mod.count / stats.schedules_count) * 100) 
                    : 0;
                  return (
                    <div key={key} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" style={{ color: mod.color }} />
                          <span className="font-medium">{mod.name}</span>
                        </div>
                        <span className="text-sm text-gray-500">{mod.count} horários</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ width: `${percentage}%`, backgroundColor: mod.color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Horários por Dia da Semana</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  {WEEKDAYS.map(day => (
                    <div key={day.id} className="text-center">
                      <div className="text-sm text-gray-500 mb-2">{day.name.split('-')[0]}</div>
                      <div className="text-3xl font-bold text-purple-600">
                        {schedules.filter(s => s.weekday === day.id).length}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog: Criar/Editar Horário */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSchedule ? 'Editar Horário' : 'Novo Horário'}
            </DialogTitle>
            <DialogDescription>
              Configure os detalhes do horário da aula extra
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dia da Semana</Label>
                <Select 
                  value={scheduleForm.weekday} 
                  onValueChange={(v) => setScheduleForm(p => ({...p, weekday: v}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map(day => (
                      <SelectItem key={day.id} value={day.id}>{day.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Modalidade</Label>
                <Select 
                  value={scheduleForm.modality_id} 
                  onValueChange={(v) => setScheduleForm(p => ({...p, modality_id: v}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {modalities.map(mod => (
                      <SelectItem key={mod.id} value={mod.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: mod.color }} />
                          {mod.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Início</Label>
                <Input 
                  type="time" 
                  value={scheduleForm.start_time}
                  onChange={(e) => setScheduleForm(p => ({...p, start_time: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label>Término</Label>
                <Input 
                  type="time" 
                  value={scheduleForm.end_time}
                  onChange={(e) => setScheduleForm(p => ({...p, end_time: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label>Saída (opcional)</Label>
                <Input 
                  type="time" 
                  value={scheduleForm.departure_time}
                  onChange={(e) => setScheduleForm(p => ({...p, departure_time: e.target.value}))}
                  placeholder="Ex: 07:50"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Período Pedagógico</Label>
                <Select 
                  value={scheduleForm.pedagogy_period} 
                  onValueChange={(v) => setScheduleForm(p => ({...p, pedagogy_period: v}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODS.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ordem de Exibição</Label>
                <Input 
                  type="number" 
                  min="1"
                  value={scheduleForm.display_order}
                  onChange={(e) => setScheduleForm(p => ({...p, display_order: parseInt(e.target.value) || 1}))}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch 
                checked={scheduleForm.is_contraturno}
                onCheckedChange={(v) => setScheduleForm(p => ({...p, is_contraturno: v}))}
              />
              <Label>Horário no Contraturno</Label>
            </div>

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea 
                value={scheduleForm.notes}
                onChange={(e) => setScheduleForm(p => ({...p, notes: e.target.value}))}
                placeholder="Ex: Saída às 07h50"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Turmas ({scheduleForm.classes.length} selecionadas)</Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                <div className="grid grid-cols-3 gap-2">
                  {classes.map(cls => (
                    <label 
                      key={cls.id} 
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        scheduleForm.classes.includes(cls.id) 
                          ? 'bg-purple-100 border-purple-300 border' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <input 
                        type="checkbox"
                        checked={scheduleForm.classes.includes(cls.id)}
                        onChange={() => toggleClassSelection(cls.id)}
                        className="rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">{cls.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveSchedule} className="bg-purple-600 hover:bg-purple-700">
              <Save className="h-4 w-4 mr-2" />
              {editingSchedule ? 'Salvar Alterações' : 'Criar Horário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Criar/Editar Comunicado */}
      <Dialog open={announcementDialogOpen} onOpenChange={setAnnouncementDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Editar Comunicado' : 'Novo Comunicado'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input 
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm(p => ({...p, title: e.target.value}))}
                placeholder="Ex: PREZADAS FAMÍLIAS"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Conteúdo</Label>
              <Textarea 
                value={announcementForm.content}
                onChange={(e) => setAnnouncementForm(p => ({...p, content: e.target.value}))}
                placeholder="Digite o conteúdo do comunicado..."
                rows={6}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select 
                  value={String(announcementForm.priority)} 
                  onValueChange={(v) => setAnnouncementForm(p => ({...p, priority: parseInt(v)}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Alta</SelectItem>
                    <SelectItem value="2">2 - Média</SelectItem>
                    <SelectItem value="3">3 - Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-7">
                <Switch 
                  checked={announcementForm.active}
                  onCheckedChange={(v) => setAnnouncementForm(p => ({...p, active: v}))}
                />
                <Label>Ativo</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnouncementDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAnnouncement} className="bg-purple-600 hover:bg-purple-700">
              <Save className="h-4 w-4 mr-2" />
              {editingAnnouncement ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar Exclusão */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este horário? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          {itemToDelete && (
            <div className="py-4 px-4 bg-red-50 rounded-lg">
              <p className="font-medium">{itemToDelete.modality_name}</p>
              <p className="text-sm text-gray-600">
                {WEEKDAYS.find(d => d.id === itemToDelete.weekday)?.name} - {itemToDelete.start_time} às {itemToDelete.end_time}
              </p>
              <p className="text-sm text-gray-500">
                {itemToDelete.class_names?.join(', ')}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteSchedule}>
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
